import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Development-only logging utility
 * Logs messages only in development environment
 */
export const devLog = {
  log: (...args: any[]) => {
    if (import.meta.env.DEV) {
      console.log(...args);
    }
  },
  warn: (...args: any[]) => {
    if (import.meta.env.DEV) {
      console.warn(...args);
    }
  },
  error: (...args: any[]) => {
    // Always log errors, even in production
    console.error(...args);
  },
};

/**
 * Check if the app is running in embedded mode (inside an iframe in eProducer)
 */
export const isEmbedded = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get('embed') === 'true';
};

/**
 * Send a message to the parent window when in embedded mode
 */
export const postMessageToParent = (type: string, data?: Record<string, unknown>) => {
  if (isEmbedded() && window.parent !== window) {
    window.parent.postMessage({ type, ...data }, '*');
  }
};

/**
 * Extract invite ID from URL path
 * Handles URLs like /invite/:inviteId
 */
export const extractInviteIdFromPath = (): string | null => {
  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);

  // Check if path starts with 'invite' and has an invite ID
  if (segments[0] === 'invite' && segments[1]) {
    // Decode URI component in case invite ID contains special characters
    return decodeURIComponent(segments[1]);
  }

  return null;
};

/**
 * Extract a self-interview check-in id from URL path
 * Handles the check-in SMS links: /interview/:interviewId (`si_…`)
 */
export const extractCheckInIdFromPath = (): string | null => {
  const segments = window.location.pathname.split('/').filter(Boolean);

  if (segments[0] === 'interview' && segments[1]) {
    return decodeURIComponent(segments[1]);
  }

  return null;
};

/**
 * The key on a texted Archive interview link (/interview/:interviewId?k=… or
 * /invite/:token?k=…), which signs its recipient in to answer it without a
 * sign-in screen. Null when the link has none.
 */
export const extractLinkKey = (): string | null => {
  if (!extractCheckInIdFromPath() && !extractInviteIdFromPath()) return null;
  return new URLSearchParams(window.location.search).get('k') || null;
};

/** Drop the link key from the address bar once it has signed the tab in. */
export const clearLinkKey = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('k')) return;
  url.searchParams.delete('k');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
};

/**
 * Get the appropriate token key for localStorage
 * For invite routes: trainToken_${inviteId}
 * For regular routes: trainToken
 */
export const getTokenKey = (): string => {
  const inviteId = extractInviteIdFromPath();
  return inviteId ? `trainToken_${inviteId}` : 'trainToken';
};

/**
 * Store authentication token in localStorage with appropriate key
 */
export const storeAuthToken = (token: string): void => {
  const tokenKey = getTokenKey();
  localStorage.setItem(tokenKey, token);
};

/**
 * Retrieve authentication token from localStorage with appropriate key
 * Falls back to base trainToken if invite-specific token not found
 */
export const getAuthToken = (): string | null => {
  const tokenKey = getTokenKey();
  const token = localStorage.getItem(tokenKey);
  
  // If we're on an invite route and no invite-specific token exists,
  // fall back to the base trainToken (user might be already logged in)
  if (!token && tokenKey !== 'trainToken') {
    return localStorage.getItem('trainToken');
  }
  
  return token;
};

/**
 * Remove authentication token from localStorage with appropriate key
 */
export const removeAuthToken = (): void => {
  const tokenKey = getTokenKey();
  localStorage.removeItem(tokenKey);
};

/**
 * Check if this is the user's first visit today
 * Uses localStorage to track the last visit date
 */
const LAST_VISIT_KEY = 'proxi_last_visit_date';

export const isFirstVisitToday = (): boolean => {
  const today = new Date().toDateString();
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY);

  if (lastVisit !== today) {
    localStorage.setItem(LAST_VISIT_KEY, today);
    return true;
  }

  return false;
};
